import { LegacyCardContent, LegacyCardHeader } from "../../card/LegacyCard";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { useOrderInvoiceUrl } from "../../../hooks/api/v2/order/useOrderInvoiceUrl";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useBuildStripeDashboardUrl } from "../../../utils/stripeDashboardUrl";

import { BuyerOnly } from "../../authFilter/BuyerOnly";
import { SupplierOnly } from "../../authFilter/SupplierOnly";
import { Button, H5, Icon } from "@sellar-io/components";
import { OrderAddress } from "../OrderAddress";
import { OrderInvoicesSentTo } from "../OrderInvoicesSentTo";

export type OrderSummaryInvoiceProps = {
  invoiceFinalizedAt?: Date;
  cancelledAt?: Date | null;
  orderId: number;
  stripeInvoiceId?: string | null;
  paymentStatus: OrderGetResponseType["paymentStatus"];
  paymentType: OrderGetResponseType["paymentType"];
  InvoiceAddress: OrderGetResponseType["InvoiceAddress"];
  invoiceEmails: string[];
  stripeAccountId?: string | null;
  payment: OrderGetResponseType["Payment"];
};

export const OrderInvoiceSummary = (
  props: OrderSummaryInvoiceProps,
): React.ReactElement => {
  const { business } = useSellarCurrentBusiness();
  const buildStripeDashboardUrl = useBuildStripeDashboardUrl();
  const invoiceUrlQuery = useOrderInvoiceUrl(props.orderId, {
    enabled:
      !!(
        props.invoiceFinalizedAt ||
        props.paymentStatus === "open" ||
        props.paymentStatus === "paid"
      ) &&
      !!business?.Retailer &&
      !!props.stripeInvoiceId,
  });

  return (
    <>
      <div>
        <LegacyCardHeader>
          <div className={"flex items-center justify-between"}>
            <H5>Invoice</H5>
            {props.paymentType !== "cardPaymentWithDeposit" && (
              <>
                <BuyerOnly>
                  {props.paymentType === "stripe" &&
                    props.paymentStatus === "paid" &&
                    (invoiceUrlQuery.data?.invoiceUrl ||
                      invoiceUrlQuery.data?.invoicePdf) &&
                    !props.cancelledAt && (
                      <Button
                        onClick={() =>
                          window.open(
                            invoiceUrlQuery.data.invoicePdf ??
                              invoiceUrlQuery.data.invoiceUrl,
                            "_blank",
                          )
                        }
                      >
                        <Icon size={0.7} icon={"OPEN_IN_NEW"} />
                        VAT Invoice
                      </Button>
                    )}
                </BuyerOnly>
                <SupplierOnly>
                  {props.payment &&
                    props.payment.status === "paid" &&
                    props.stripeAccountId && (
                      <CheckoutPaymentViewInvoiceButton
                        stripePaymentIntentId={props.payment.paymentIntentId}
                        stripeAccountId={props.stripeAccountId}
                      />
                    )}
                  {props.stripeInvoiceId && props.stripeAccountId && (
                    <Button
                      onClick={(e) => {
                        if (!props.stripeInvoiceId || !props.stripeAccountId)
                          return;
                        e.preventDefault();
                        window.open(
                          buildStripeDashboardUrl({
                            type: "invoice",
                            stripeAccountId: props.stripeAccountId,
                            id: props.stripeInvoiceId,
                          }),
                          "_blank",
                        );
                      }}
                    >
                      <Icon size={0.7} icon={"OPEN_IN_NEW"} />
                      View on Stripe
                    </Button>
                  )}
                </SupplierOnly>
              </>
            )}
          </div>
        </LegacyCardHeader>
        <LegacyCardContent>
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <OrderAddress address={props.InvoiceAddress} title="Address" />
              <OrderInvoicesSentTo emailAddresses={props.invoiceEmails} />
            </div>
          </div>
        </LegacyCardContent>
      </div>
    </>
  );
};

export const CheckoutPaymentViewInvoiceButton = ({
  stripePaymentIntentId,
  stripeAccountId,
}: {
  stripePaymentIntentId: string;
  stripeAccountId: string;
}) => {
  const buildStripeDashboardUrl = useBuildStripeDashboardUrl();
  return (
    <Button
      onClick={(e) => {
        e.preventDefault();
        window.open(
          buildStripeDashboardUrl({
            type: "payment",
            stripeAccountId,
            id: stripePaymentIntentId,
          }),
          "_blank",
        );
      }}
    >
      <Icon size={0.7} icon={"OPEN_IN_NEW"} />
      View on Stripe
    </Button>
  );
};
